import { useQueryCache } from '@pinia/colada';
import { AxiosError } from 'axios';
import { acceptHMRUpdate, defineStore } from 'pinia';

import { useAlertsStore } from '@tallo/design-system';
import { FileDto } from '@tallo/file-manager';
import { companyPortfoliosQueryKey } from '@tallo/investor/company';
import {
  Coordinates,
  PropertyDto,
  PropertyStatus,
  ScreeningSensitivity,
  SelfGuidedAccessMethod,
} from '@tallo/property';

import { SaveAccessibilityDto } from '../dtos/save-accessibility.dto.interface';
import { SaveAmenitiesDto } from '../dtos/save-amenities.dto.interface';
import { SaveIncludedUtilitiesDto } from '../dtos/save-included-utilities.dto.interface';
import { SaveLeaseConditionsDto } from '../dtos/save-lease-condition.dto.interface';
import { SaveLocationDto } from '../dtos/save-location.dto.interface';
import { SaveParkingDto } from '../dtos/save-parking.dto.interface';
import { SavePetPolicyDto } from '../dtos/save-pet-policy.dto.interface';
import { SavePropertySpecificationsDto } from '../dtos/save-property-specifications.dto.interface';
import { SavePropertyRenterRequirementsDto } from '../dtos/save-renter-requirements.dto.interface';
import { SaveSyndicationDto } from '../dtos/save-syndication.dto.interface';
import { SaveTourTypesDto } from '../dtos/save-tour-types.dto.interface';
import { ToggleAutoRefreshDto } from '../dtos/toggle-auto-refresh.dto.interface';
import { portfolioQueryKey } from '../queries/portfolio.query';
import { getPropertySyndicationPlatformsQueryKey } from '../queries/property-syndication-platforms.query';
import propertyListingService from '../services/property-listing.service';
import propertyService from '../services/property.service';

interface PropertyState {
  property: PropertyDto | undefined;
  isLoading: boolean;
  isLoaded: boolean;
  /** The unit the backend last answered 404 for: deleted since the link was made, or another company's */
  missingPropertyId: string | null;
}

export const usePropertyStore = defineStore({
  id: 'property',
  state: (): PropertyState => {
    return {
      property: undefined,
      isLoading: false,
      isLoaded: false,
      missingPropertyId: null,
    };
  },
  getters: {
    /**
     * Asked by id, so a page that has moved on to another unit never shows the not-found state of the
     * one before, whichever load answered last
     */
    isMissing(state): (propertyId: string | undefined) => boolean {
      return (propertyId) => !!propertyId && state.missingPropertyId === propertyId;
    },

    coordinates(state): Coordinates | null {
      const location = state.property?.location;

      if (!location?.latitude || !location?.longitude) {
        return null;
      }

      return {
        latitude: location.latitude,
        longitude: location.longitude,
      };
    },

    images(state): FileDto[] {
      return (state.property?.images || []).sort((a, b) => a!.order - b!.order);
    },
  },
  actions: {
    setProperty(property: PropertyDto) {
      this.property = property;
    },

    setImages(images: FileDto[]): void {
      this.property!.images = images;
    },

    async loadProperty(propertyId: string) {
      const alertStore = useAlertsStore();
      this.isLoading = true;
      this.isLoaded = false;

      try {
        const { data } = await propertyService.getById(propertyId);
        this.setProperty(data);
        this.isLoaded = true;
      } catch (error) {
        if (error instanceof AxiosError && error.response?.status === 404) {
          // The page says so itself, with a way back, rather than toasting over nothing
          this.missingPropertyId = propertyId;
        } else {
          alertStore.error('Failed to load property');
        }
      } finally {
        this.isLoading = false;
      }
    },

    async deleteProperty(propertyId: string) {
      const queryCache = useQueryCache();

      await propertyService.delete(propertyId);

      queryCache.invalidateQueries({ key: [portfolioQueryKey] });
    },

    async startPropertyListing(propertyId: string, syndicationData?: SaveSyndicationDto) {
      const queryCache = useQueryCache();
      const alertsStore = useAlertsStore();

      try {
        const { data } = await propertyListingService.listProperty(propertyId, syndicationData);

        Object.assign(this.property!, data);

        await Promise.all([
          queryCache.invalidateQueries({ key: [portfolioQueryKey] }),
          queryCache.invalidateQueries({ key: getPropertySyndicationPlatformsQueryKey(propertyId) }),
        ]);
      } catch (error) {
        alertsStore.somethingWentWrong();
      }
    },

    async pausePropertyListing(propertyId: string) {
      const queryCache = useQueryCache();
      const alertsStore = useAlertsStore();

      try {
        const { data } = await propertyListingService.pauseProperty(propertyId);

        Object.assign(this.property!, data);
        alertsStore.success(`<b>${this.property?.displayName}</b> has been unlisted`);

        await Promise.all([
          queryCache.invalidateQueries({ key: [portfolioQueryKey] }),
          queryCache.invalidateQueries({ key: getPropertySyndicationPlatformsQueryKey(propertyId) }),
        ]);
      } catch (error) {
        alertsStore.somethingWentWrong();
      }
    },

    async markAsRentedOut(propertyId: string) {
      const queryCache = useQueryCache();
      const { data } = await propertyListingService.markAsRentedOut(propertyId);

      Object.assign(this.property!, data);

      await Promise.all([
        queryCache.invalidateQueries({ key: [portfolioQueryKey] }),
        queryCache.invalidateQueries({ key: getPropertySyndicationPlatformsQueryKey(propertyId) }),
      ]);
    },

    async toggleAutoRefresh(autoRefresh: boolean) {
      const alertsStore = useAlertsStore();
      const propertyId = this.property?.id;

      if (!propertyId) {
        alertsStore.error('Property not found');
        return;
      }

      try {
        const toggleData: ToggleAutoRefreshDto = { autoRefresh };
        const { data } = await propertyListingService.toggleAutoRefresh(propertyId, toggleData);

        if (data.success) {
          this.property!.autoRefresh = data.autoRefresh;

          if (data.autoRefresh) {
            alertsStore.success('Listing optimization enabled');
          } else {
            alertsStore.info('Listing optimization disabled');
          }
        }
      } catch (error) {
        alertsStore.error('Failed to update listing optimization setting');
      }
    },

    async updateDisplayName(displayName: string) {
      const queryCache = useQueryCache();

      const response = await propertyService.updateProperty(this.property!.id, { displayName });

      this.property!.displayName = response.data.displayName;

      queryCache.invalidateQueries({ key: [portfolioQueryKey] });
    },

    async saveDescription(description: string) {
      const { data } = await propertyService.updateProperty(this.property?.id as string, { description });

      this.property!.description = data.description;
    },

    async saveHighlights(highlights: string) {
      const { data } = await propertyService.updateProperty(this.property?.id as string, { highlights });

      this.property!.highlights = data.highlights;
    },

    async saveStatus(status: PropertyStatus) {
      const queryCache = useQueryCache();
      const { data } = await propertyService.updateProperty(this.property?.id as string, { status });

      this.property!.status = data.status;
      queryCache.invalidateQueries({ key: [portfolioQueryKey] });
    },

    async generateDescription(): Promise<string> {
      const { data } = await propertyService.generateDescription(this.property?.id as string);

      return data;
    },

    async saveRenterRequirements(payload: SavePropertyRenterRequirementsDto) {
      const renterRequirementsId = this.property?.renterRequirements?.id;
      const { data } = renterRequirementsId
        ? await propertyService.updateRenterRequirements(this.property?.id!, renterRequirementsId, payload)
        : await propertyService.createRenterRequirements(this.property?.id!, payload);

      this.property!.renterRequirements = data;
    },

    async updateScreeningSensitivity(screeningSensitivity: ScreeningSensitivity) {
      const renterRequirementsId = this.property?.renterRequirements?.id;
      const { data } = renterRequirementsId
        ? await propertyService.updateScreeningSensitivity(
            this.property?.id!,
            renterRequirementsId,
            screeningSensitivity,
          )
        : await propertyService.createRenterRequirements(this.property?.id!, { screeningSensitivity });

      this.property!.renterRequirements = data;
    },

    async saveSpecifications(payload: SavePropertySpecificationsDto) {
      const specificationId = this.property?.specifications?.id;
      const { data } = specificationId
        ? await propertyService.updateSpecifications(this.property?.id!, specificationId, payload)
        : await propertyService.createSpecifications(this.property?.id!, payload);

      this.property!.specifications = data;
    },

    async saveAccessibility(payload: SaveAccessibilityDto) {
      const accessibilityId = this.property?.accessibility?.id;
      const { data } = accessibilityId
        ? await propertyService.updateAccessibility(this.property?.id!, accessibilityId, payload)
        : await propertyService.createAccessibility(this.property?.id!, payload);

      this.property!.accessibility = data;
    },

    async saveAmenities(payload: SaveAmenitiesDto) {
      const amenitiesId = this.property?.amenities?.id;
      const { data } = amenitiesId
        ? await propertyService.updateAmenities(this.property?.id!, amenitiesId, payload)
        : await propertyService.createAmenities(this.property?.id!, payload);

      this.property!.amenities = data;
    },

    async saveIncludedUtilities(payload: SaveIncludedUtilitiesDto) {
      const includedUtilitiesId = this.property?.includedUtilities?.id;
      const { data } = includedUtilitiesId
        ? await propertyService.updateIncludedUtilities(this.property?.id!, includedUtilitiesId, payload)
        : await propertyService.createIncludedUtilities(this.property?.id!, payload);

      this.property!.includedUtilities = data;
    },

    async saveLeaseConditions(form: SaveLeaseConditionsDto) {
      const leaseConditionsId = this.property?.leaseConditions?.id;
      const { data } = leaseConditionsId
        ? await propertyService.updateLeaseConditions(this.property?.id!, leaseConditionsId, form)
        : await propertyService.createLeaseConditions(this.property?.id!, form);

      this.property!.leaseConditions = data;
    },

    async saveParking(payload: SaveParkingDto) {
      const parkingId = this.property?.parking?.id;
      const { data } = parkingId
        ? await propertyService.updateParking(this.property?.id!, parkingId, payload)
        : await propertyService.createParking(this.property?.id!, payload);

      this.property!.parking = data;
    },

    async savePetPolicy(payload: SavePetPolicyDto) {
      const petPolicyId = this.property?.petPolicy?.id;
      const { data } = petPolicyId
        ? await propertyService.updatePetPolicy(this.property?.id!, petPolicyId, payload)
        : await propertyService.createPetPolicy(this.property?.id!, payload);

      this.property!.petPolicy = data;
    },

    async saveTourTypes(payload: SaveTourTypesDto) {
      // identityVerificationEnabled rides along as-is: it applies to every tour type the property
      // offers, and the forms only show the toggle while at least one tour type is selected.
      const { data } = await propertyService.updateProperty(this.property?.id as string, payload);

      this.property!.allowsInPersonTours = data.allowsInPersonTours;
      this.property!.allowsVirtualTours = data.allowsVirtualTours;
      // Update self-guided if present in response (backward compatible)
      if ('allowsSelfGuidedTours' in data) {
        this.property!.allowsSelfGuidedTours = (data as any).allowsSelfGuidedTours ?? null;
      }
      this.property!.selfGuidedAccessMethod = data.selfGuidedAccessMethod ?? null;
      this.property!.identityVerificationEnabled = data.identityVerificationEnabled ?? false;
    },

    async saveAccessMethod(selfGuidedAccessMethod: SelfGuidedAccessMethod | null) {
      const { data } = await propertyService.updateProperty(this.property?.id as string, { selfGuidedAccessMethod });

      this.property!.selfGuidedAccessMethod = data.selfGuidedAccessMethod ?? null;
    },

    /** `portfolioId` only counts when this creates the unit; where an existing unit sits is changed on its Team tab */
    async saveLocation(payload: SaveLocationDto, portfolioId?: string) {
      const queryCache = useQueryCache();
      const propertyId = this.property?.id;

      if (!propertyId) {
        const { data } = await propertyService.create({ location: payload, portfolioId });

        this.setProperty(data);

        if (portfolioId) {
          // The portfolio grew and changed; the refresh is a nicety, the unit is filed either way
          queryCache.invalidateQueries({ key: [companyPortfoliosQueryKey] }).catch(() => undefined);

          // Filing runs after the unit is saved, so a failure there leaves a unit that exists, unassigned
          if (!data.portfolioId) {
            useAlertsStore().error(
              "The listing was created, but it couldn't be added to that portfolio. You can file it from its Team tab.",
            );
          }
        }
      } else {
        const locationId = this.property?.location?.id! as string;
        const { data } = await propertyService.updateLocation(propertyId as string, locationId, payload);

        const oldLocation = this.property!.location;

        if (
          oldLocation.address !== data.address ||
          oldLocation.city !== data.city ||
          oldLocation.state !== data.state ||
          oldLocation.zip !== data.zip
        ) {
          this.property!.displayName = data.address;
        }

        this.property!.location = data;
      }

      queryCache.invalidateQueries({ key: [portfolioQueryKey] });
    },

    async deleteImage(imageId: string) {
      const imageIndex = this.images.findIndex(({ id }) => id === imageId);

      if (imageIndex > -1) {
        this.property?.images.splice(imageIndex, 1);
      }

      await propertyService.deleteImage(this.property!.id, imageId);
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(usePropertyStore, import.meta.hot));
}
